import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * A dialog.
 *
 * Built on `<dialog>` rather than a positioned div, because the native element
 * brings three things that are tedious and easy to get wrong by hand: the top
 * layer (so nothing can overlap it, including the dock), a focus trap, and
 * Escape to close. All three are accessibility requirements rather than
 * niceties, and a hand-rolled overlay usually ships with none of them.
 *
 * `showModal()` from an effect rather than the `open` attribute: the attribute
 * renders a non-modal dialog, which has neither the backdrop nor the trap.
 */
export function Modal({
  title,
  onClose,
  wide = false,
  banner,
  children,
}: {
  title: string;
  onClose: () => void;
  /** For dialogs built out of side-by-side choices rather than a column of prose. */
  wide?: boolean;
  /**
   * Artwork across the top, in place of the title bar.
   *
   * For the handful of dialogs that explain what something *is* rather than ask
   * a question about it - where a row reading "Vibed Boosts" over a paragraph
   * defining Vibed Boosts is a line of chrome saying nothing. The title is
   * still passed and still reaches a screen reader through `aria-label`; it
   * just stops being drawn twice.
   */
  banner?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog || dialog.open) return;

    dialog.showModal();

    return () => dialog.close();
  }, []);

  return (
    <dialog
      ref={ref}
      aria-label={title}
      // Escape fires `cancel`, and the default would close the element without
      // telling React - leaving the parent still rendering a dialog that is no
      // longer on screen.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Clicking the backdrop closes. The target is the dialog itself only
        // when the click landed outside its content box.
        if (event.target === ref.current) onClose();
      }}
      className={`m-auto rounded-[var(--radius-card)] border border-line bg-surface p-0 text-ink backdrop:bg-black/60 ${
        wide ? 'w-[min(46rem,calc(100vw-2rem))]' : 'w-[min(32rem,calc(100vw-2rem))]'
      } ${banner ? 'overflow-hidden' : ''}`}
    >
      {banner ? (
        /*
          Clipped by the dialog rather than rounded here, so a banner can paint
          to its own edges without knowing what shape it is being poured into.
        */
        <div className="relative">
          {banner}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)] border border-line bg-surface/70 text-soft backdrop-blur-sm transition-colors hover:bg-surface hover:text-ink"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-pill)] text-soft transition-colors hover:bg-raised hover:text-ink"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
          </button>
        </div>
      )}

      <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
    </dialog>
  );
}
